/**
 * Team (experiment) hub: overview, stream grid with `StreamCard`, settings, invites, charts.
 *
 * Where: `/teams/:teamId` route.
 * Uses: `teamApi`, `StreamCreateModal`, `TeamSettings`, `AggregatedStatsCard`, snackbars for feedback.
 * Behavior: tabbed or sectioned layout; handles member management and stream lifecycle actions.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Snackbar,
  Card,
  CardContent,
  Tabs,
  Tab,
  Divider,
  Skeleton,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  IconButton,
} from '@mui/material';
import {
  Save as SaveIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import type { InfiniteData } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { apiClient } from '../api/client';
import type { TeamItem, TeamRoleItem, TeamUser, TeamUserResponse } from '@/types/team';
import { teamApi } from '@/api/team';

// Extended team interface that includes notes
interface TeamDetails extends TeamItem {
  notes?: string | null;
  end_at?: string | null;
}

interface TeamUpdateData {
  name: string;
  notes: string;
}

interface FormErrors {
  name?: string;
  notes?: string;
}

const getTimeAgo = (dateString: string) => {
  const now = new Date();
  const created = new Date(dateString);
  const diffInDays = Math.floor((now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) return 'Today';
  if (diffInDays === 1) return '1 day ago';
  if (diffInDays < 7) return `${diffInDays} days ago`;
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
  return `${Math.floor(diffInDays / 30)} months ago`;
};

const getRoleLabel = (roleName: string) => {
  switch (roleName) {
    case 'OWNER':
      return 'Owner';
    case 'SERVICE_MANAGER':
      return 'Service Manager';
    case 'SERVICE_MEMBER':
      return 'Service Member';
    default:
      return roleName;
  }
};

export const TeamDetails: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(0);

  // Basic team info form state
  const [formData, setFormData] = useState<TeamUpdateData>({
    name: '',
    notes: '',
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  // Team members state
  const [userSearch, setUserSearch] = useState('');
  const [selectedUserForRole, setSelectedUserForRole] = useState<TeamUser | null>(null);
  const [selectedRole, setSelectedRole] = useState<'SERVICE_MANAGER' | 'SERVICE_MEMBER'>('SERVICE_MEMBER');
  const trimmedUserSearch = userSearch.trim();
  const shouldSearchUsers = trimmedUserSearch.length > 0;

  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Fetch team details
  const {
    data: teamResponse,
    isLoading: isLoadingTeam,
    error: teamError,
  } = useQuery({
    queryKey: ['team', currentUser?.key, teamId],
    queryFn: async () => {
      const response = await apiClient.get(`/teams/${teamId}/`, {
        params: {
          add_owner_info: true,
        },
      });
      return response.data;
    },
    enabled: !!teamId,
  });

  const team = teamResponse?.output as TeamDetails | undefined;

  // Fetch team roles (members)
  const { data: rolesData, isLoading: isLoadingRoles } = useQuery({
    queryKey: ['team-roles', currentUser?.key, teamId],
    queryFn: () => teamApi.getTeamRoles(teamId!),
    enabled: !!teamId,
  });

  const teamRoles: TeamRoleItem[] = rolesData?.output?.items || [];

  // Determine if current user can manage the team
  const isAdmin = currentUser?.is_staff || currentUser?.is_superuser;
  const isOwner = team?.owner?.key === currentUser?.key;
  const isServiceManager = teamRoles.some(
    (role) => role.user?.key === currentUser?.key && role.name === 'SERVICE_MANAGER'
  );
  const canManageTeam = isAdmin || isOwner || isServiceManager;

  // User search query
  const {
    data: usersPages,
    isLoading: isLoadingUsers,
    fetchNextPage: fetchNextUsersPage,
    hasNextPage: hasNextUsersPage,
    isFetchingNextPage: isFetchingNextUsersPage,
  } = useInfiniteQuery<
    TeamUserResponse,
    Error,
    InfiniteData<TeamUserResponse, string | undefined>,
    [string, string],
    string | undefined
  >({
    queryKey: ['team-settings-users-search', trimmedUserSearch],
    queryFn: ({ pageParam }) =>
      teamApi.searchAllUsers({
        page_token: pageParam,
        max_results: 10,
        search: trimmedUserSearch,
      }),
    getNextPageParam: (lastPage) => lastPage.output.next_page_token,
    initialPageParam: undefined,
    enabled: shouldSearchUsers,
  });

  const searchedUsers: TeamUser[] = usersPages?.pages.flatMap((page: TeamUserResponse) => page.output.items) ?? [];
  const usersLoadMoreRef = useRef<HTMLDivElement | null>(null);

  // Infinite scroll observer
  useEffect(() => {
    if (!shouldSearchUsers || !usersLoadMoreRef.current || !hasNextUsersPage || !fetchNextUsersPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextUsersPage && !isFetchingNextUsersPage) {
          fetchNextUsersPage();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(usersLoadMoreRef.current);
    return () => observer.disconnect();
  }, [hasNextUsersPage, isFetchingNextUsersPage, fetchNextUsersPage, shouldSearchUsers]);

  // Initialize form data when team loads
  useEffect(() => {
    if (team) {
      setFormData({
        name: team.name || '',
        notes: team.notes || '',
      });
      setHasChanges(false);
      setErrors({});
    }
  }, [team]);

  // Check for changes
  useEffect(() => {
    if (team) {
      const hasModifications =
        formData.name !== (team.name || '') || formData.notes !== (team.notes || '');
      setHasChanges(hasModifications);
    }
  }, [formData, team]);

  // Validation function
  const validateForm = (): FormErrors => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Team name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Team name must be at least 2 characters long';
    } else if (formData.name.trim().length > 512) {
      newErrors.name = 'Team name cannot exceed 512 characters';
    }

    if (formData.notes && formData.notes.length > 5000) {
      newErrors.notes = 'Notes cannot exceed 5000 characters';
    }

    return newErrors;
  };

  // Update team mutation
  const updateTeamMutation = useMutation({
    mutationFn: (data: TeamUpdateData) => teamApi.updateTeam(team!.key, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team', team!.key] });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      setHasChanges(false);
      showToast('Team settings updated successfully', 'success');
    },
    onError: (error: any) => {
      console.error('Failed to update team:', error);
      showToast(error?.response?.data?.error_messages?.[0] || 'Failed to update team settings', 'error');
    },
  });

  // Add role mutation
  const addRoleMutation = useMutation({
    mutationFn: (data: { user_uuid: string; role: 'SERVICE_MANAGER' | 'SERVICE_MEMBER' }) =>
      teamApi.addTeamRole(team!.key, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-roles', currentUser?.key, teamId] });
      setSelectedUserForRole(null);
      setUserSearch('');
      showToast('User role added successfully', 'success');
    },
    onError: (error: any) => {
      console.error('Failed to add role:', error);
      showToast(error?.response?.data?.error_messages?.[0] || 'Failed to add user role', 'error');
    },
  });

  // Remove role mutation
  const removeRoleMutation = useMutation({
    mutationFn: (roleUuid: string) => teamApi.removeTeamRole(roleUuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-roles', currentUser?.key, teamId] });
      showToast('User role removed successfully', 'success');
    },
    onError: (error: any) => {
      console.error('Failed to remove role:', error);
      showToast(error?.response?.data?.error_messages?.[0] || 'Failed to remove user role', 'error');
    },
  });

  // Resend invite mutation
  const resendInviteMutation = useMutation({
    mutationFn: (roleUuid: string) => teamApi.resendInvite(roleUuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-roles', currentUser?.key, teamId] });
      queryClient.invalidateQueries({ queryKey: ['pending-invites-count'] });
      showToast('Invite resent successfully', 'success');
    },
    onError: (error: any) => {
      console.error('Failed to resend invite:', error);
      showToast(error?.response?.data?.error_messages?.[0] || 'Failed to resend invite', 'error');
    },
  });

  const handleInputChange =
    (field: keyof TeamUpdateData) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = event.target.value;
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));

      if (errors[field]) {
        setErrors((prev) => ({
          ...prev,
          [field]: undefined,
        }));
      }
    };

  const handleSave = () => {
    if (!hasChanges) return;

    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setErrors({});
    updateTeamMutation.mutate({
      ...formData,
      name: formData.name.trim(),
    });
  };

  const handleReset = () => {
    if (team) {
      setFormData({
        name: team.name || '',
        notes: team.notes || '',
      });
      setHasChanges(false);
      setErrors({});
    }
  };

  const handleAddUserRole = () => {
    if (!selectedUserForRole) return;
    const userUuid = (selectedUserForRole.uuid || selectedUserForRole.key) as string;
    addRoleMutation.mutate({ user_uuid: userUuid, role: selectedRole });
  };

  const handleRemoveRole = (roleUuid: string) => {
    removeRoleMutation.mutate(roleUuid);
  };

  const handleResendInvite = (roleUuid: string) => {
    resendInviteMutation.mutate(roleUuid);
  };

  const handleCloseToast = useCallback(() => {
    setToast((prev) => ({ ...prev, open: false }));
  }, []);

  const showToast = useCallback((message: string, severity: 'success' | 'error') => {
    setToast({ open: true, message, severity });
  }, []);

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      setActiveTab(newValue);
    },
    [],
  );

  const isPending =
    updateTeamMutation.isPending || addRoleMutation.isPending || removeRoleMutation.isPending || resendInviteMutation.isPending;

  if (isLoadingTeam) {
    return (
      <Box sx={{ my: 2, mx: 2 }}>
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="text" width={200} height={40} />
          <Skeleton variant="text" width={300} height={24} />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' }, gap: 4 }}>
          <Card>
            <CardContent>
              <Skeleton variant="text" width={150} height={32} />
              <Skeleton variant="text" width={100} height={24} sx={{ mt: 2 }} />
              <Skeleton variant="text" width={120} height={24} sx={{ mt: 2 }} />
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Skeleton variant="text" width={200} height={32} />
              <Skeleton variant="text" width="100%" height={56} sx={{ mt: 2 }} />
              <Skeleton variant="text" width="100%" height={56} sx={{ mt: 2 }} />
            </CardContent>
          </Card>
        </Box>
      </Box>
    );
  }

  if (teamError || !team) {
    return (
      <Box sx={{ my: 2, mx: 2 }}>
        <Alert severity="error">Failed to load team details. Please try again.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ my: 2, mx: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          Team Details
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage team information, members, and settings.
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' }, gap: 4 }}>
        {/* Left Column - Team Summary */}
        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                {team.name}
              </Typography>
              <Divider sx={{ my: 2 }} />

              {/* Owner */}
              {team.owner && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Owner
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {team.owner.name || team.owner.email || 'Unknown'}
                  </Typography>
                  {team.owner.email && team.owner.name && (
                    <Typography variant="caption" color="text.secondary">
                      {team.owner.email}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Created Date */}
              {team.created_at && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Created
                  </Typography>
                  <Typography variant="body1">{getTimeAgo(team.created_at)}</Typography>
                </Box>
              )}

              {/* Started Date */}
              {team.started_at && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Started
                  </Typography>
                  <Typography variant="body1">
                    {new Date(team.started_at).toLocaleDateString()}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Right Column - Tabs */}
        <Card>
          <CardContent>
            <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
              <Tab label="Basic Team Information" />
              <Tab label="Team Members" />
            </Tabs>

            {activeTab === 0 && (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Basic Team Information
                  </Typography>
                </Box>

                {canManageTeam ? (
                  <>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <TextField
                        fullWidth
                        label="Team Name"
                        variant="outlined"
                        value={formData.name}
                        onChange={handleInputChange('name')}
                        disabled={isPending}
                        error={!!errors.name}
                        helperText={errors.name || 'The display name for this team'}
                        required
                      />

                      <TextField
                        fullWidth
                        label="Notes"
                        variant="outlined"
                        multiline
                        rows={6}
                        value={formData.notes}
                        onChange={handleInputChange('notes')}
                        disabled={isPending}
                        error={!!errors.notes}
                        helperText={errors.notes || 'Additional notes about this team'}
                        placeholder="Enter any notes about this team..."
                      />
                    </Box>

                    {updateTeamMutation.isError && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        Failed to update team settings. Please try again.
                      </Alert>
                    )}

                    {updateTeamMutation.isSuccess && !hasChanges && (
                      <Alert severity="success" sx={{ mt: 2 }}>
                        Team settings updated successfully!
                      </Alert>
                    )}

                    <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                      <Button
                        variant="contained"
                        startIcon={updateTeamMutation.isPending ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                        onClick={handleSave}
                        disabled={!hasChanges || isPending}
                        sx={{ minWidth: 120 }}
                      >
                        {updateTeamMutation.isPending ? 'Saving...' : 'Save Changes'}
                      </Button>

                      <Button variant="outlined" onClick={handleReset} disabled={!hasChanges || isPending}>
                        Reset
                      </Button>
                    </Box>
                  </>
                ) : (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      You don't have permission to edit team information.
                    </Typography>
                  </Box>
                )}
              </>
            )}

            {activeTab === 1 && (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Team Members
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {canManageTeam
                    ? 'Manage who has access to this team and their roles.'
                    : 'View team members and their roles.'}
                </Typography>

                {/* Add User Role Section - Only show if user can manage team */}
                {canManageTeam && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                      Add User
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField
                        placeholder="Search users by name or email..."
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                        fullWidth
                        disabled={isPending}
                        size="small"
                        helperText={!shouldSearchUsers ? 'Type to search for users' : undefined}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                        }}
                      />

                      {shouldSearchUsers && (
                        <Box
                          sx={{
                            maxHeight: 200,
                            overflow: 'auto',
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1,
                          }}
                        >
                          {isLoadingUsers && searchedUsers.length === 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                              <CircularProgress size={24} />
                            </Box>
                          )}
                          <List dense>
                            {searchedUsers.map((user) => {
                              const label = user.name || user.email || 'User';
                              const secondary = user.email && user.name ? user.email : '';
                              const isSelected = selectedUserForRole?.key === user.key;
                              return (
                                <ListItemButton
                                  key={user.key}
                                  selected={isSelected}
                                  onClick={() => setSelectedUserForRole(user)}
                                >
                                  <ListItemText primary={label} secondary={secondary} />
                                </ListItemButton>
                              );
                            })}
                          </List>
                          <div ref={usersLoadMoreRef} style={{ height: 16 }} />
                          {isFetchingNextUsersPage && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                              <CircularProgress size={18} />
                            </Box>
                          )}
                          {searchedUsers.length === 0 && !isLoadingUsers && (
                            <Box sx={{ p: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                No users found.
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      )}

                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                          <InputLabel>Role</InputLabel>
                          <Select
                            value={selectedRole}
                            onChange={(e) => setSelectedRole(e.target.value as 'SERVICE_MANAGER' | 'SERVICE_MEMBER')}
                            label="Role"
                            disabled={isPending}
                          >
                            <MenuItem value="SERVICE_MANAGER">Service Manager</MenuItem>
                            <MenuItem value="SERVICE_MEMBER">Service Member</MenuItem>
                          </Select>
                        </FormControl>
                        <Button
                          startIcon={addRoleMutation.isPending ? <CircularProgress size={16} /> : <AddIcon />}
                          onClick={handleAddUserRole}
                          disabled={!selectedUserForRole || isPending}
                          variant="outlined"
                          size="small"
                        >
                          Add User
                        </Button>
                        {selectedUserForRole && (
                          <Typography variant="body2" color="text.secondary">
                            Selected: {selectedUserForRole.name || selectedUserForRole.email}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                )}

                {canManageTeam && <Divider sx={{ my: 2 }} />}

                {/* Existing Roles List */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    Current Members
                  </Typography>

                  {isLoadingRoles ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : teamRoles.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No members assigned to this team yet.
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {teamRoles.map((role) => (
                        <Box
                          key={role.key}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            p: 1.5,
                            bgcolor: 'action.hover',
                            borderRadius: 1,
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {role.user?.name || role.user?.email || 'Unknown User'}
                            </Typography>
                            {role.user?.email && role.user?.name && (
                              <Typography variant="caption" color="text.secondary">
                                {role.user.email}
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                minWidth: 120,
                                color: role.name === 'OWNER' || role.name === 'SERVICE_MANAGER' ? 'primary.main' : 'text.secondary',
                                fontWeight: role.name === 'OWNER' || role.name === 'SERVICE_MANAGER' ? 600 : 400,
                              }}
                            >
                              {getRoleLabel(role.name)}
                            </Typography>
                            {role.status && role.status !== 'ACCEPTED' && (
                              <Chip
                                label={role.status === 'PENDING' ? 'Pending' : 'Rejected'}
                                color={role.status === 'PENDING' ? 'warning' : 'error'}
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Box>
                          {canManageTeam && (
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              {role.status === 'REJECTED' && (
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleResendInvite(role.key)}
                                  disabled={isPending}
                                  title="Resend invite"
                                >
                                  <RefreshIcon fontSize="small" />
                                </IconButton>
                              )}
                              {(role.can_remove || role.status === 'PENDING' || role.status === 'REJECTED') && (
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleRemoveRole(role.key)}
                                  disabled={isPending}
                                  title="Remove invite"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Toast Notification */}
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseToast} severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
